import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useField } from "formik";
import React, { useEffect, useReducer, useRef } from "react";
import countryDialCodes from "./country_dial_codes.json";

// Google libphonenumber
let PhoneNumberUtil = require("google-libphonenumber").PhoneNumberUtil;
let PNF = require("google-libphonenumber").PhoneNumberFormat;
let phoneUtil = PhoneNumberUtil.getInstance();

interface Country {
  name: string;
  dialCode: string;
  isoCode: string;
  flag: string;
}

interface PhoneNumberInputProps {
  label: string;
  name: string;
  placeholder?: string;
  defaultCountry?: string;
}

interface State {
  countries: Country[];
  selectedCountry: Country | null;
  inputValue: string;
  isValid: boolean;
  isInitialisedFromFormik: boolean;

  // What we sync back to formik
  internationalNumber: string | null;
}

const initialState: State = {
  countries: countryDialCodes.filter((c) => c.dialCode !== "BREAK"),
  selectedCountry: null,
  inputValue: "",
  isValid: true, // empty is ok
  isInitialisedFromFormik: false,

  internationalNumber: null,
};

function reducer(state: State, action: any) {
  let newState = { ...state };

  switch (action.type) {
    case "INPUT_ON_CHANGE":
      return { ...state, inputValue: action.payload };

    case "SET_SELECTED_COUNTRY":
      newState.selectedCountry = action.payload;
      break;

    case "INPUT_ON_BLUR":
      newState.inputValue = action.payload ?? "";
      break;

    case "INITIALIZE_FROM_FORMIK":
      const { fieldValue, defaultCountry } = action.payload;

      let c = state.countries.find(
        (country) => country.isoCode === defaultCountry
      );
      if (c) {
        newState.selectedCountry = c;
      }

      newState.inputValue = fieldValue ?? "";

      newState.isInitialisedFromFormik = true;
      break;

    default:
      return state;
  }

  // Here we have either blured or initialised or the country has been changed

  if (newState.inputValue) {
    try {
      let number = phoneUtil.parseAndKeepRawInput(
        newState.inputValue,
        newState.selectedCountry?.isoCode ?? "GB"
      );
      let deducedCountry = phoneUtil.getRegionCodeForNumber(number);

      if (phoneUtil.isValidNumber(number)) {
        let internationalFormat = phoneUtil.format(number, PNF.INTERNATIONAL);
        newState.internationalNumber = internationalFormat;

        // Remove country code and clean up the format
        let trimmedFormat = internationalFormat
          .replace(/^\+?\d+\s*/, "")
          .trim();

        newState.inputValue = trimmedFormat;

        // Update country selector if different from parsed number
        if (
          deducedCountry &&
          deducedCountry !== newState.selectedCountry?.isoCode
        ) {
          let newSelectedCountry = state.countries.find(
            (country) => country.isoCode === deducedCountry
          );
          newState.selectedCountry = newSelectedCountry ?? state.countries[0];
        }

        newState.isValid = true;
      } else {
        newState.isValid = false;
        newState.internationalNumber = null;
      }
    } catch (e) {
      newState.isValid = false;
      newState.internationalNumber = null;
    }
  } else {
    // If input is empty, reset to valid state
    newState.isValid = true;
    newState.internationalNumber = null;
  }

  return newState;
}
export function PhoneNumberInput({
  label,
  name,
  placeholder = "Phone number",
  defaultCountry = "GB",
}: PhoneNumberInputProps) {
  let [field, meta, helpers] = useField(name);

  let inputRef = useRef<HTMLInputElement>(null);
  let [state, dispatch] = useReducer(reducer, initialState);
  let [isFocused, setIsFocused] = React.useState(false);

  useEffect(() => {
    helpers.setTouched(true);
    if (state.isValid) {
      helpers.setValue(state.internationalNumber);
    } else {
      helpers.setValue(state.inputValue); // allow the yup schema to validate
    }
  }, [state.internationalNumber, state.inputValue, state.isValid]);

  useEffect(() => {
    // Tell formik the field has an error if the phone number is invalid
    if (!state.isValid) {
      helpers.setError("Invalid phone number");
      helpers.setTouched(true);
    } else {
      helpers.setError(undefined);
    }
  }, [state.isValid]);

  let setSelectedCountry = (country: Country) => {
    dispatch({ type: "SET_SELECTED_COUNTRY", payload: country });
  };

  let error = meta.touched && meta.error;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch({ type: "INPUT_ON_CHANGE", payload: value });
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    dispatch({
      type: "INPUT_ON_BLUR",
      payload: e.target.value,
    });
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    helpers.setTouched(true);
  };

  const handleCountryFocus = () => {
    setIsFocused(true);
  };

  const handleCountryBlur = () => {
    // Use setTimeout to check if focus moved to the input
    setTimeout(() => {
      if (inputRef.current !== document.activeElement) {
        setIsFocused(false);
      }
    }, 0);
  };

  if (!state.isInitialisedFromFormik) {
    dispatch({
      type: "INITIALIZE_FROM_FORMIK",
      payload: {
        fieldValue: field.value,
        defaultCountry,
      },
    });
  }

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div
        className={`flex rounded-md shadow-sm transition-all ${
          isFocused ? "ring-2 ring-green-500" : ""
        } ${error || !state.isValid ? "ring-2 ring-red-500" : ""}`}
      >
        {/* Country Selector */}
        <Listbox value={state.selectedCountry} onChange={setSelectedCountry}>
          <div className="relative">
            <ListboxButton
              tabIndex={-1}
              onFocus={handleCountryFocus}
              onBlur={handleCountryBlur}
              className="relative h-10 pl-3 pr-8 bg-gray-50 border border-gray-300 rounded-l-md hover:bg-gray-100 cursor-pointer focus:outline-none"
            >
              <div className="flex items-center space-x-2">
                {state.selectedCountry && (
                  <>
                    <img
                      src={state.selectedCountry.flag}
                      alt={state.selectedCountry.name}
                      className="w-5 h-3 object-cover rounded"
                    />
                    <span className="text-sm text-gray-700 font-medium">
                      {state.selectedCountry.dialCode}
                    </span>
                  </>
                )}
              </div>
              <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </ListboxButton>

            <Transition
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions className="absolute z-50 mt-1 max-h-60 w-80 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {state.countries.map((country) => (
                  <ListboxOption
                    key={country.isoCode}
                    value={country}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-3 pr-9 ${
                        active ? "bg-green-100 text-green-900" : "text-gray-900"
                      }`
                    }
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={country.flag}
                        alt={country.name}
                        className="w-5 h-3 object-cover rounded"
                      />
                      <span className="font-medium text-sm">
                        {country.dialCode}
                      </span>
                      <span className="text-sm text-gray-500 truncate">
                        {country.name}
                      </span>
                    </div>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        </Listbox>

        {/* Phone Number Input */}
        <input
          ref={inputRef}
          type="tel"
          value={state.inputValue}
          placeholder={placeholder}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          className="flex-1 h-10 px-3 border-l-0 border-gray-300 rounded-r-md focus:outline-none focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
        />
      </div>

      {(error || !state.isValid) && (
        <p className="text-sm text-red-600">
          {error || "Invalid phone number"}
        </p>
      )}

      {/* Helper text */}
      <p className="text-xs text-gray-500"></p>
    </div>
  );
}
